import { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { GoogleMap, Marker, useJsApiLoader, OverlayView } from '@react-google-maps/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faSpinner,
  faHeart as faHeartSolid,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartOutline } from '@fortawesome/free-regular-svg-icons';
import { toast } from 'react-toastify';
import { useLazyQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { GET_CARS_NEAR_ME } from '../../services/carService/queries';
import { combinePickerDateTimeGetUTC } from '../../helpers/combinePickerDateTimeGetUTC';
import { URLS } from '../../../constants/urls';
import { formatAddress } from '../../helpers/formatAddress';
import { useFavorites } from '../../hooks/useFavorites';
import { authStore } from '../../../store/authStore';
import { SCREENS } from '../responsive';
import { createPriceMarkerIcon } from './createPriceMarkerIcon';
import { convertDistance, EARTH_RADIUS_MILES } from '../../utils/units';
import { useBrandLocale } from '../../hooks/useBrandLocale';

// Mobile Card Styles
const MobileCarCard = styled.div`
  ${tw`
    absolute
    bg-white
    flex
    flex-row
    z-20
    cursor-pointer
    overflow-hidden
    box-border
    md:hidden
  `}
  left: 12px;
  right: 12px;
  height: 8rem;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  bottom: max(12px, env(safe-area-inset-bottom, 0px));
`;

const MobileCardImageContainer = styled.div`
  ${tw`
    w-32
    h-full
    relative
    flex-shrink-0
  `}
`;

const MobileCardImage = styled.img`
  ${tw`
    w-full
    h-full
    object-cover
  `}
  border-radius: 12px 0 0 12px;
`;

const MobileCardContent = styled.div`
  ${tw`
    flex-1
    p-3
    flex
    flex-col
    justify-center
    relative
  `}
`;

const MobileCardTitle = styled.h3`
  ${tw`
    text-base
    font-bold
    text-gray-900
    mb-1
    line-clamp-2
  `}
`;

const MobileCardLocation = styled.p`
  ${tw`
    text-xs
    text-gray-500
    mb-2
    line-clamp-1
  `}
`;

const MobileCardPriceConfig = styled.div`
  ${tw`
    flex
    items-baseline
    gap-1
  `}
`;

const MobilePriceAmount = styled.span`
  ${tw`
    text-xl
    font-bold
    text-gray-900
  `}
`;

const MobilePriceLabel = styled.span`
  ${tw`
    text-xs
    text-gray-400
  `}
`;

const MapContainer = styled.div`
  ${tw`
    w-full
    relative
    min-w-0
  `}
  height: 100%;
  min-height: 0;
  @media (min-width: ${SCREENS.md}) {
    min-height: 500px;
  }
`;

const SearchThisAreaButtonContainer = styled.div<{ $show: boolean }>`
  position: absolute;
  top: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: ${props => (props.$show ? 'block' : 'none')};
`;

const SearchThisAreaButton = styled.button<{ $isLoading?: boolean }>`
  ${tw`
    flex
    items-center
    gap-2
    px-6
    py-3
    bg-red-500
    text-white
    font-semibold
    rounded-full
    shadow-lg
    transition-colors
    duration-200
    cursor-pointer
  `}
  ${props => props.$isLoading && tw`opacity-70 cursor-wait`}
  &:hover {
    ${tw`bg-red-600`}
  }
`;

const CarInfoCard = styled.div`
  ${tw`
    absolute
    bottom-4
    left-4
    right-4
    bg-white
    rounded-lg
    shadow-lg
    p-4
    z-10
    max-h-48
    overflow-y-auto
  `}
`;

const CarInfoTitle = styled.h3`
  ${tw`
    text-lg
    font-semibold
    mb-2
  `}
`;

const CarInfoText = styled.p`
  ${tw`
    text-sm
    text-gray-600
  `}
`;

// 标记上方的车辆卡片样式
const MarkerCarCard = styled.div`
  ${tw`
    hidden
    md:block
    bg-white
    rounded-lg
    shadow-xl
    overflow-hidden
    cursor-pointer
    w-64
    border
    border-gray-200
  `}
`;

const CardImageContainer = styled.div`
  ${tw`
    relative
    w-full
    h-40
  `}
`;

const CardImage = styled.img`
  ${tw`
    w-full
    h-full
    object-cover
  `}
`;

const CardContent = styled.div`
  ${tw`
    p-3
  `}
`;

const CardTitle = styled.h3`
  ${tw`
    text-base
    font-bold
    mb-1
  `}
`;

const CardLocation = styled.p`
  ${tw`
    text-xs
    text-gray-600
    mb-2
  `}
`;

const CardPrice = styled.div`
  ${tw`
    text-right
  `}
`;

const PriceAmount = styled.span`
  ${tw`
    font-bold
    text-lg
    text-green-600
  `}
`;

const PriceLabel = styled.p`
  ${tw`
    text-xs
    text-gray-500
  `}
`;

const CardFavoriteButton = styled.button<{ $isFavorite?: boolean }>`
  ${tw`
    absolute
    top-2
    right-2
    bg-white
    border-none
    z-10
    flex
    items-center
    justify-center
    cursor-pointer
  `}
  padding: 7px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  svg {
    color: ${props => (props.$isFavorite ? '#E11D48' : '#595959')};
    font-size: 16px;
  }

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

const libraries: ('places' | 'drawing' | 'geometry' | 'visualization')[] = ['places'];

export interface Car {
  id: string;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  location: {
    latitude: number;
    longitude: number;
    address: string;
    city: string;
    state: string;
  };
  weekdayPrice?: number | null;
  thumbnailUrl?: string | null;
  images?: Array<{ imageUrl?: string | null }> | null;
  calculatedPrice?: {
    totalPrice?: number | null;
  } | null;
  distanceMiles?: number;
}

export interface MapProps {
  cars: Car[];
  searchParams: {
    latitude: number;
    longitude: number;
    startDate: Date;
    endDate: Date;
    startTime: string;
    returnTime: string;
    timezone: string;
    driverAge: number;
    type?: string;
    locationId?: number;
    returnLocationId?: number;
    focusRadiusMiles?: number;
    lockToSearchLocation?: boolean;
  };
  onCarsUpdate?: (cars: Car[]) => void;
  onCarSelect?: (car: Car | null) => void;
  selectedCarId?: string;
  favorites?: any[];
  onToggleFavorite?: (carId: string, isCurrentlyFavorite: boolean) => void;
  localFavoriteIds?: Set<string>;
  isCarFavorited?: (carId: string) => boolean;
}

const defaultCenter = {
  lat: 37.7749,
  lng: -122.4194,
};

const defaultZoom = 10;

// 浅色主题 + 蓝色/紫色渐变地图样式
const lightThemeMapStyles: google.maps.MapTypeStyle[] = [
  {
    elementType: 'geometry',
    stylers: [{ color: '#f5f7fa' }],
  },
  {
    elementType: 'geometry.stroke',
    stylers: [{ color: '#e8ecf1' }],
  },
  {
    elementType: 'labels.text.fill',
    stylers: [{ color: '#334155' }],
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#f5f7fa' }],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry',
    stylers: [{ color: '#d9e1eb' }],
  },
  {
    featureType: 'administrative.country',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#667eea' }, { weight: 2 }],
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#4f46e5' }],
  },
  {
    featureType: 'administrative.province',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#7c9cce' }, { weight: 1.5 }],
  },
  {
    featureType: 'landscape.natural',
    elementType: 'geometry',
    stylers: [{ color: '#ecf0f5' }],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: '#e8ecf1' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#334155' }],
  },
  {
    featureType: 'poi.business',
    elementType: 'geometry',
    stylers: [{ color: '#e8ecf1' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#dbeafe' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#059669' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#e0e7f1' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#cbd5e1' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#475569' }],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry',
    stylers: [{ color: '#d9e1eb' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#667eea' }, { lightness: -10 }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#4d5aff' }],
  },
  {
    featureType: 'road.highway.controlled_access',
    elementType: 'geometry',
    stylers: [{ color: '#764ba2' }, { weight: 1.5 }],
  },
  {
    featureType: 'road.local',
    elementType: 'geometry',
    stylers: [{ color: '#e0e7f1' }],
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#e8ecf1' }],
  },
  {
    featureType: 'transit.line',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#667eea' }, { weight: 1 }],
  },
  {
    featureType: 'transit.station',
    elementType: 'geometry',
    stylers: [{ color: '#d9e1eb' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#c5d9f1' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#667eea' }],
  },
];

// 深色主题 + 蓝色/紫色渐变地图样式
const darkThemeMapStyles: google.maps.MapTypeStyle[] = [
  {
    elementType: 'geometry',
    stylers: [{ color: '#1a1f2e' }],
  },
  {
    elementType: 'geometry.stroke',
    stylers: [{ color: '#2a3142' }],
  },
  {
    elementType: 'labels.text.fill',
    stylers: [{ color: '#7c9cce' }],
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#1a1f2e' }],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry',
    stylers: [{ color: '#3a4152' }],
  },
  {
    featureType: 'administrative.country',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#4d5aff' }, { weight: 2 }],
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6b7ee6' }],
  },
  {
    featureType: 'administrative.province',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#667eea' }, { weight: 1.5 }],
  },
  {
    featureType: 'landscape.natural',
    elementType: 'geometry',
    stylers: [{ color: '#242f3e' }],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: '#2d3748' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#7c9cce' }],
  },
  {
    featureType: 'poi.business',
    elementType: 'geometry',
    stylers: [{ color: '#2d3748' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#1e4620' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#62b362' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#3a4152' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#2a3142' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#7c9cce' }],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry',
    stylers: [{ color: '#464d52' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#667eea' }, { lightness: 5 }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#4d5aff' }],
  },
  {
    featureType: 'road.highway.controlled_access',
    elementType: 'geometry',
    stylers: [{ color: '#764ba2' }, { weight: 1.5 }],
  },
  {
    featureType: 'road.local',
    elementType: 'geometry',
    stylers: [{ color: '#3a4152' }],
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#2d3748' }],
  },
  {
    featureType: 'transit.line',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#667eea' }, { weight: 1 }],
  },
  {
    featureType: 'transit.station',
    elementType: 'geometry',
    stylers: [{ color: '#3a4152' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#0f1d35' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#667eea' }],
  },
];

export function Map({
  cars: initialCars,
  searchParams,
  onCarsUpdate,
  onCarSelect,
  selectedCarId,
  favorites: externalFavorites,
  onToggleFavorite,
  isCarFavorited: externalIsCarFavorited,
}: MapProps) {
  // Spec 028: distances in this site's unit (the API answers in miles).
  const { distanceUnit } = useBrandLocale();
  const navigate = useNavigate();
  const [cars, setCars] = useState<Car[]>(initialCars);
  const [selectedCar, setSelectedCar] = useState<Car | null>(null);
  const [hoveredCarId, setHoveredCarId] = useState<string | null>(null);
  // Use external favorites if provided, otherwise use local hook
  const { toggleFavorite: localToggleFavorite, favorites: localFavorites } = useFavorites();
  const favorites = externalFavorites ?? localFavorites;
  const toggleFavorite = onToggleFavorite ?? localToggleFavorite;
  const isAuthenticated = authStore.isAuthenticated;

  // Use external isCarFavorited if provided, otherwise create local version
  // Note: getMyFavorites returns car objects with 'id', while addFavorite returns Favorite objects with 'carId'
  const isCarFavorited = useCallback(
    (carId: string) => {
      if (externalIsCarFavorited) {
        return externalIsCarFavorited(carId);
      }
      return favorites.some((f: any) => f.id === carId || f.carId === carId);
    },
    [externalIsCarFavorited, favorites],
  );
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>({
    lat: searchParams.latitude || defaultCenter.lat,
    lng: searchParams.longitude || defaultCenter.lng,
  });
  const [mapBounds, setMapBounds] = useState<google.maps.LatLngBounds | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  // Track if user manually selected a marker
  const userSelectedMarkerRef = useRef<boolean>(false);
  // Track if search button should be visible
  const [showSearchButton, setShowSearchButton] = useState(true);
  // Track if map was programmatically moved (not user drag)
  const isProgrammaticMoveRef = useRef<boolean>(false);
  const shouldLockToSearchLocation = Boolean(
    searchParams.lockToSearchLocation && searchParams.latitude && searchParams.longitude,
  );

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey || '',
    libraries,
  });

  const [searchCarsInArea, { loading: searchLoading }] = useLazyQuery(GET_CARS_NEAR_ME, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const newCars = (data?.getCarsNearMe || []) as any[];
      // Convert to Car format for map display
      const mapCars: Car[] = newCars.map((car: any) => ({
        id: car.id,
        make: car.make,
        model: car.model,
        year: car.year,
        location: {
          latitude: car.latitude ?? 0,
          longitude: car.longitude ?? 0,
          address: car.location?.address || '',
          city: car.location?.city || '',
          state: car.location?.state || '',
        },
        weekdayPrice: car.weekdayPrice,
        thumbnailUrl: car.thumbnailUrl,
        images: car.images,
        calculatedPrice: car.calculatedPrice,
        distanceMiles: car.distanceMiles,
        latitude: car.latitude,
        longitude: car.longitude,
      }));
      setCars(mapCars);
      if (onCarsUpdate) {
        // Pass the full car data (not just Car format) so SearchResultPage can use all fields
        onCarsUpdate(newCars);
      }
      setIsSearching(false);
      // Hide button after search completes
      setShowSearchButton(false);
      toast.success(`Found ${newCars.length} cars in this area`);
    },
    onError: error => {
      setIsSearching(false);
      toast.error('Failed to search cars in this area');
    },
  });

  // Reset user selection flag when search params change (new search)
  useEffect(() => {
    userSelectedMarkerRef.current = false;
  }, [searchParams.latitude, searchParams.longitude, searchParams.startDate, searchParams.endDate]);

  const fitRadiusToCenter = useCallback(
    (center: google.maps.LatLngLiteral, radiusMiles: number) => {
      if (!mapRef.current || !radiusMiles) {
        return;
      }
      const latOffset = radiusMiles / 69;
      const lngOffset = radiusMiles / (69 * Math.cos((center.lat * Math.PI) / 180));
      const bounds = new google.maps.LatLngBounds(
        { lat: center.lat - latOffset, lng: center.lng - lngOffset },
        { lat: center.lat + latOffset, lng: center.lng + lngOffset },
      );
      isProgrammaticMoveRef.current = true;
      mapRef.current.fitBounds(bounds);
      setMapCenter(center);
    },
    [],
  );

  // Update cars when initialCars changes
  useEffect(() => {
    // 如果用户已经手动选择了标记，完全跳过自动居中逻辑
    if (userSelectedMarkerRef.current) {
      return;
    }

    setCars(initialCars || []);
    // Hide button when new search results are loaded
    setShowSearchButton(false);
    isProgrammaticMoveRef.current = true;

    // 自动居中逻辑：只在初始化或搜索参数改变时执行
    if (shouldLockToSearchLocation) {
      const newCenter = { lat: searchParams.latitude, lng: searchParams.longitude };
      if (mapRef.current) {
        if (searchParams.focusRadiusMiles) {
          fitRadiusToCenter(newCenter, searchParams.focusRadiusMiles);
        } else {
          isProgrammaticMoveRef.current = true;
          mapRef.current.setCenter(newCenter);
        }
      }
      setMapCenter(newCenter);
      return;
    }
    if (initialCars && initialCars.length > 0) {
      const firstCarWithCoords = initialCars.find(car => {
        const lat = (car as any).latitude;
        const lng = (car as any).longitude;
        return (
          lat &&
          lng &&
          !isNaN(Number(lat)) &&
          !isNaN(Number(lng)) &&
          Number(lat) !== 0 &&
          Number(lng) !== 0
        );
      });

      if (firstCarWithCoords) {
        const lat = (firstCarWithCoords as any).latitude;
        const lng = (firstCarWithCoords as any).longitude;
        const newCenter = { lat: Number(lat), lng: Number(lng) };

        // 直接设置地图中心，不依赖状态更新
        if (mapRef.current) {
          isProgrammaticMoveRef.current = true;
          mapRef.current.setCenter(newCenter);
        }
        setMapCenter(newCenter);
      } else if (searchParams.latitude && searchParams.longitude) {
        const newCenter = { lat: searchParams.latitude, lng: searchParams.longitude };
        if (mapRef.current) {
          isProgrammaticMoveRef.current = true;
          mapRef.current.setCenter(newCenter);
        }
        setMapCenter(newCenter);
      }
    } else if (searchParams.latitude && searchParams.longitude) {
      const newCenter = { lat: searchParams.latitude, lng: searchParams.longitude };
      if (mapRef.current) {
        isProgrammaticMoveRef.current = true;
        mapRef.current.setCenter(newCenter);
      }
      setMapCenter(newCenter);
    }
  }, [
    initialCars,
    searchParams.latitude,
    searchParams.longitude,
    searchParams.focusRadiusMiles,
    shouldLockToSearchLocation,
    fitRadiusToCenter,
    isMapReady,
  ]);

  // Force markers to re-render when cars data arrives after map is ready
  useEffect(() => {
    if (isMapReady && cars.length > 0 && mapRef.current) {
      // Trigger a small map update to force marker re-render
      const currentCenter = mapRef.current.getCenter();
      if (currentCenter) {
        // Use setTimeout to ensure this happens after React has updated
        setTimeout(() => {
          if (mapRef.current) {
            // Trigger bounds change to force re-render
            const bounds = mapRef.current.getBounds();
            if (bounds) {
              setMapBounds(bounds);
            }
          }
        }, 100);
      }
    }
  }, [isMapReady, cars.length]);

  const handleMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      const bounds = map.getBounds();
      if (bounds) {
        setMapBounds(bounds);
      }

      // Set isMapReady after a short delay to ensure map is fully initialized
      setTimeout(() => {
        setIsMapReady(true);

        // If cars are already available, ensure markers are rendered
        if (cars.length > 0) {
          // Trigger a bounds change to force marker re-render
          const currentBounds = map.getBounds();
          if (currentBounds) {
            setMapBounds(currentBounds);
          }
        }
      }, 100);

      // Center on first car if available (use current cars state)
      if (cars.length > 0 && !shouldLockToSearchLocation) {
        const firstCar = cars.find(car => {
          const lat = (car as any).latitude;
          const lng = (car as any).longitude;
          return lat && lng && !isNaN(Number(lat)) && !isNaN(Number(lng));
        });
        if (firstCar) {
          const lat = (firstCar as any).latitude;
          const lng = (firstCar as any).longitude;
          isProgrammaticMoveRef.current = true;
          map.setCenter({
            lat: Number(lat),
            lng: Number(lng),
          });
        }
      }
    },
    [cars, shouldLockToSearchLocation],
  );

  useEffect(() => {
    if (!isMapReady || !mapRef.current || !shouldLockToSearchLocation) {
      return;
    }
    const newCenter = { lat: searchParams.latitude, lng: searchParams.longitude };
    if (searchParams.focusRadiusMiles) {
      fitRadiusToCenter(newCenter, searchParams.focusRadiusMiles);
    } else {
      isProgrammaticMoveRef.current = true;
      mapRef.current.setCenter(newCenter);
      mapRef.current.setZoom(13);
      setMapCenter(newCenter);
    }
  }, [
    isMapReady,
    shouldLockToSearchLocation,
    searchParams.latitude,
    searchParams.longitude,
    searchParams.focusRadiusMiles,
    fitRadiusToCenter,
  ]);

  // When cars update after map is ready, ensure markers are rendered
  useEffect(() => {
    if (isMapReady && mapRef.current && cars.length > 0) {
      // Force a re-render by updating map center slightly
      const currentCenter = mapRef.current.getCenter();
      if (currentCenter) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          if (mapRef.current && currentCenter) {
            // Trigger a bounds change to force marker re-render
            const bounds = mapRef.current.getBounds();
            if (bounds) {
              setMapBounds(bounds);
            }
          }
        });
      }
    }
  }, [isMapReady, cars.length]);

  const handleMapBoundsChanged = useCallback(() => {
    if (mapRef.current) {
      const bounds = mapRef.current.getBounds();
      if (bounds) {
        setMapBounds(bounds);
      }
      // If this is a user-initiated drag (not programmatic), show the button
      if (!isProgrammaticMoveRef.current) {
        setShowSearchButton(true);
      }
      // Reset the flag after a short delay to allow for programmatic moves to complete
      if (isProgrammaticMoveRef.current) {
        setTimeout(() => {
          isProgrammaticMoveRef.current = false;
        }, 500);
      }
    }
  }, []);

  const calculateSearchRadius = useCallback((bounds: google.maps.LatLngBounds) => {
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const center = bounds.getCenter();

    // Calculate distance using Haversine formula
    const R = EARTH_RADIUS_MILES; // spec 028: the shared radius, in miles (the API's unit)
    const dLat = ((ne.lat() - sw.lat()) * Math.PI) / 180;
    const dLng = ((ne.lng() - sw.lng()) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((sw.lat() * Math.PI) / 180) *
        Math.cos((ne.lat() * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const diagonalRadiusMiles = (R * c * Math.sqrt(2)) / 2;

    return {
      minRadius: 0,
      maxRadius: Math.min(diagonalRadiusMiles, 20),
      centerLat: center.lat(),
      centerLng: center.lng(),
    };
  }, []);

  const handleSearchThisArea = useCallback(() => {
    if (!mapBounds) {
      toast.error('Map bounds not available');
      return;
    }

    setIsSearching(true);
    const { centerLat, centerLng, maxRadius } = calculateSearchRadius(mapBounds);

    const startAtTimestamp = combinePickerDateTimeGetUTC(
      searchParams.startDate,
      searchParams.startTime,
      searchParams.timezone,
    );

    const endAtTimestamp = combinePickerDateTimeGetUTC(
      searchParams.endDate,
      searchParams.returnTime,
      searchParams.timezone,
    );

    searchCarsInArea({
      variables: {
        input: {
          latitude: centerLat,
          longitude: centerLng,
          minRadiusMiles: 0,
          maxRadiusMiles: maxRadius,
          startAt: startAtTimestamp,
          endAt: endAtTimestamp,
          driverAge: searchParams.driverAge,
          type: searchParams.type || 'CAR',
        },
      },
    });
  }, [mapBounds, searchParams, calculateSearchRadius, searchCarsInArea]);

  // 简单直接的逻辑：点击标记就聚焦到该标记
  const handleMarkerClick = useCallback(
    (car: Car, event?: google.maps.MapMouseEvent) => {
      // Try multiple possible locations for coordinates (same as marker rendering)
      // Priority: car.latitude > car.location.latitude
      const targetLat = (car as any).latitude ?? car.location?.latitude;
      const targetLng = (car as any).longitude ?? car.location?.longitude;

      if (!targetLat || !targetLng || !mapRef.current) {
        return;
      }

      const targetPosition = {
        lat: Number(targetLat),
        lng: Number(targetLng),
      };

      // 如果点击了同一个标记，则关闭卡片
      if (selectedCar?.id === car.id) {
        setSelectedCar(null);
        userSelectedMarkerRef.current = false;
        onCarSelect?.(null);
        return;
      }

      // 标记用户已手动选择，阻止自动居中
      userSelectedMarkerRef.current = true;

      // 更新状态（用于显示卡片）
      setSelectedCar(car);

      // 检查标记是否在当前视口内
      const bounds = mapRef.current.getBounds();
      const isInViewport = bounds ? bounds.contains(targetPosition) : false;

      // 先设置缩放级别（如果需要），确保有合适的视野
      const currentZoom = mapRef.current.getZoom() || defaultZoom;
      if (currentZoom < 13) {
        mapRef.current.setZoom(13);
      }

      // 使用 panTo 进行平滑动画移动到标记位置
      // panTo 提供平滑的动画效果，比 setCenter 更友好
      isProgrammaticMoveRef.current = true;
      mapRef.current.panTo(targetPosition);

      // 同步更新 mapCenter 状态（用于 GoogleMap 的 center prop，保持状态一致性）
      setMapCenter(targetPosition);

      // 使用 setTimeout 验证居中是否成功（在动画完成后）
      setTimeout(() => {
        if (mapRef.current) {
          const currentCenter = mapRef.current.getCenter();
          if (currentCenter) {
            const centerLat = currentCenter.lat();
            const centerLng = currentCenter.lng();
            const latDiff = Math.abs(centerLat - targetPosition.lat);
            const lngDiff = Math.abs(centerLng - targetPosition.lng);

            // 如果中心点距离目标位置超过 0.0001 度（约 10 米），使用 setCenter 精确居中
            // 注意：只在动画完成后进行微调，避免打断动画
            if (latDiff > 0.0001 || lngDiff > 0.0001) {
              isProgrammaticMoveRef.current = true;
              mapRef.current.setCenter(targetPosition);
              setMapCenter(targetPosition);
            } else {
            }
          }
        }
      }, 500); // 等待 panTo 动画完成（通常需要 300-500ms）

      if (onCarSelect) {
        onCarSelect(car);
      }
    },
    [onCarSelect, selectedCar, defaultZoom],
  );

  if (loadError) {
    return (
      <MapContainer>
        <div className="flex items-center justify-center h-full p-4">
          <p className="text-red-500">Error loading map. Please check your API key.</p>
        </div>
      </MapContainer>
    );
  }

  const validCars = cars.filter(car => {
    // Try multiple possible locations for coordinates
    // Priority: car.latitude > car.location.latitude
    const lat = (car as any).latitude ?? car.location?.latitude;
    const lng = (car as any).longitude ?? car.location?.longitude;

    const isValid =
      lat != null &&
      lng != null &&
      !isNaN(Number(lat)) &&
      !isNaN(Number(lng)) &&
      Number(lat) !== 0 &&
      Number(lng) !== 0;

    if (!isValid) {
    } else {
    }

    return isValid;
  });

  return (
    <MapContainer>
      {isLoaded ? (
        <>
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={mapCenter}
            zoom={defaultZoom}
            options={{
              disableDefaultUI: false,
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: true,
              gestureHandling: 'greedy',
              styles: lightThemeMapStyles,
            }}
            onLoad={handleMapLoad}
            onIdle={() => {
              // Map is fully loaded and idle, ensure markers are rendered
              if (!isMapReady && mapRef.current) {
                setIsMapReady(true);
              }
              // Reset programmatic move flag after map becomes idle
              if (isProgrammaticMoveRef.current) {
                setTimeout(() => {
                  isProgrammaticMoveRef.current = false;
                }, 100);
              }
            }}
            onBoundsChanged={handleMapBoundsChanged}
            onDragStart={() => {
              // When user starts dragging, mark it as user-initiated
              isProgrammaticMoveRef.current = false;
              if (selectedCar) {
                setSelectedCar(null);
                userSelectedMarkerRef.current = false;
                onCarSelect?.(null);
              }
            }}
            onDragEnd={() => {
              // When user finishes dragging, show the button
              setShowSearchButton(true);
              isProgrammaticMoveRef.current = false;
            }}
            onClick={() => {
              // 点击地图时关闭卡片，并重置用户选择状态
              if (selectedCar) {
                setSelectedCar(null);
                userSelectedMarkerRef.current = false;
                onCarSelect?.(null);
                if (mapRef.current) {
                  const currentCenter = mapRef.current.getCenter();
                  if (currentCenter) {
                    // This is just updating state, not moving the map, so no flag needed
                    setMapCenter(currentCenter.toJSON());
                  }
                }
              }
            }}
          >
            {isMapReady &&
              validCars.map(car => {
                // Try multiple possible locations for coordinates
                // Priority: car.latitude > car.location.latitude
                const lat = (car as any).latitude ?? car.location?.latitude;
                const lng = (car as any).longitude ?? car.location?.longitude;

                if (!lat || !lng) {
                  return null;
                }

                const latNum = Number(lat);
                const lngNum = Number(lng);

                // Log marker coordinates for debugging

                const price = car.calculatedPrice?.totalPrice || car.weekdayPrice;
                const priceText = price ? `$${Math.round(price)}` : 'N/A';
                const isSelected = selectedCarId === car.id;

                const isHovered = hoveredCarId === car.id;

                return (
                  <Marker
                    key={car.id}
                    position={{
                      lat: latNum,
                      lng: lngNum,
                    }}
                    onClick={e => {
                      e.stop();
                      handleMarkerClick(car, e);
                    }}
                    onMouseOver={() => setHoveredCarId(car.id)}
                    onMouseOut={() => setHoveredCarId(null)}
                    title={`${car.make} ${car.model} ${car.year} - ${priceText}`}
                    icon={createPriceMarkerIcon(price, isSelected, isHovered)}
                  />
                );
              })}

            {/* 在标记上方显示车辆卡片 */}
            {selectedCar &&
              (() => {
                const lat = (selectedCar as any).latitude;
                const lng = (selectedCar as any).longitude;
                return lat && lng ? (
                  <OverlayView
                    position={{
                      lat: Number(lat),
                      lng: Number(lng),
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    getPixelPositionOffset={(width, height) => ({
                      x: -(width / 2),
                      y: -(height + 25), // 在标记上方25px
                    })}
                  >
                    <MarkerCarCard
                      onClick={e => {
                        e.stopPropagation();
                        // 阻止事件冒泡，防止触发地图的 onClick
                      }}
                      onMouseDown={e => {
                        e.stopPropagation();
                        // 阻止事件冒泡
                      }}
                    >
                      <CardImageContainer>
                        {(selectedCar.thumbnailUrl || selectedCar.images?.[0]?.imageUrl) && (
                          <CardImage
                            src={`${URLS.S3_BUCKET.CAR_THUMBNAILS}/${selectedCar.thumbnailUrl || selectedCar.images?.[0]?.imageUrl}`}
                            alt={`${selectedCar.make} ${selectedCar.model}`}
                            onClick={e => {
                              e.stopPropagation();
                              // 构建 urlParams，类似 SearchScreen.tsx 中的格式
                              const urlParams = new URLSearchParams({
                                locationId: searchParams.locationId?.toString() || '',
                                startDate: searchParams.startDate.toISOString(),
                                startTime: searchParams.startTime,
                                returnDate: searchParams.endDate.toISOString(),
                                returnTime: searchParams.returnTime,
                                driverAge: searchParams.driverAge.toString(),
                                ...(searchParams.returnLocationId && {
                                  returnLocationId: searchParams.returnLocationId.toString(),
                                }),
                              }).toString();

                              navigate(`/cars/guest/view/${selectedCar.id}?${urlParams}`);
                            }}
                            style={{ cursor: 'pointer' }}
                          />
                        )}

                        <CardFavoriteButton
                          $isFavorite={isCarFavorited(selectedCar.id)}
                          onClick={async e => {
                            e.stopPropagation();
                            if (!isAuthenticated) {
                              toast.error('Please log in to manage favorites');
                              return;
                            }
                            const currentlyFavorite = isCarFavorited(selectedCar.id);
                            // Call API (optimistic update handled by parent)
                            toggleFavorite(selectedCar.id, currentlyFavorite);
                          }}
                        >
                          <FontAwesomeIcon
                            icon={isCarFavorited(selectedCar.id) ? faHeartSolid : faHeartOutline}
                          />
                        </CardFavoriteButton>
                      </CardImageContainer>
                      <CardContent>
                        <CardTitle>
                          {selectedCar.make} {selectedCar.model} {selectedCar.year}
                        </CardTitle>
                        <CardLocation>
                          {formatAddress(selectedCar.location)}
                          {selectedCar.distanceMiles &&
                            ` • ${convertDistance(selectedCar.distanceMiles, 'mi', distanceUnit).toFixed(1)} ${distanceUnit === 'km' ? 'km' : 'mi'}`}
                        </CardLocation>
                        <CardPrice>
                          <PriceAmount>
                            $
                            {selectedCar.calculatedPrice?.totalPrice
                              ? Math.round(selectedCar.calculatedPrice.totalPrice)
                              : selectedCar.weekdayPrice || 'N/A'}{' '}
                            total
                          </PriceAmount>
                          <PriceLabel>before taxes and fees</PriceLabel>
                        </CardPrice>
                      </CardContent>
                    </MarkerCarCard>
                  </OverlayView>
                ) : null;
              })()}
          </GoogleMap>

          {/* Mobile Fixed Card - Inside map overlay */}
          {selectedCar && (
            <MobileCarCard
              onClick={() => {
                const urlParams = new URLSearchParams({
                  locationId: searchParams.locationId?.toString() || '',
                  startDate: searchParams.startDate.toISOString(),
                  startTime: searchParams.startTime,
                  returnDate: searchParams.endDate.toISOString(),
                  returnTime: searchParams.returnTime,
                  driverAge: searchParams.driverAge.toString(),
                  ...(searchParams.returnLocationId && {
                    returnLocationId: searchParams.returnLocationId.toString(),
                  }),
                }).toString();
                navigate(`/cars/guest/view/${selectedCar.id}?${urlParams}`);
              }}
            >
              <MobileCardImageContainer>
                {selectedCar.thumbnailUrl || selectedCar.images?.[0]?.imageUrl ? (
                  <MobileCardImage
                    src={`${URLS.S3_BUCKET.CAR_THUMBNAILS}/${selectedCar.thumbnailUrl || selectedCar.images?.[0]?.imageUrl}`}
                    alt={`${selectedCar.make} ${selectedCar.model}`}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-400 text-xs">No Image</span>
                  </div>
                )}
              </MobileCardImageContainer>

              <MobileCardContent>
                <MobileCardTitle>
                  {selectedCar.year} {selectedCar.make} {selectedCar.model}
                </MobileCardTitle>
                <MobileCardLocation>
                  {selectedCar.location.city}, {selectedCar.location.state}
                </MobileCardLocation>

                <MobileCardPriceConfig>
                  <MobilePriceAmount>
                    $
                    {selectedCar.calculatedPrice?.totalPrice
                      ? Math.round(selectedCar.calculatedPrice.totalPrice)
                      : selectedCar.weekdayPrice || 'N/A'}
                  </MobilePriceAmount>
                  <MobilePriceLabel>total</MobilePriceLabel>
                </MobileCardPriceConfig>
              </MobileCardContent>
            </MobileCarCard>
          )}

          <SearchThisAreaButtonContainer $show={showSearchButton && !selectedCar}>
            <SearchThisAreaButton
              onClick={handleSearchThisArea}
              $isLoading={isSearching || searchLoading}
              disabled={isSearching || searchLoading}
            >
              {isSearching || searchLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Searching...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSearch} />
                  <span>Search this area</span>
                </>
              )}
            </SearchThisAreaButton>
          </SearchThisAreaButtonContainer>
        </>
      ) : (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-red-500" />
            <p className="mt-4 text-gray-600">Loading map...</p>
          </div>
        </div>
      )}
    </MapContainer>
  );
}
